// Subscription widget helpers (description sanitizer, purchase cards, tooltips).
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.relocateSubscriptionWidget();
        this.bindSubscriptionWidgetEvents();
        this.bindSubscriptionPriceDisplay();
        this.bindPurchaseOptionCardClicks();
        this.bindSubscriptionTooltipDelay();
        this.bindSubscriptionDescriptionSanitizer();
        this.bindVariantInputSync();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      bindVariantInputSync() {
        if (typeof subscribe !== 'function') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const productInfo = this.closest('product-info');
          if (!productInfo) return;

          const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
          if (event.data?.sectionId !== sectionId) return;

          if (event.data?.variant) {
            this.syncVariantInput(event.data.variant);
          }
        });
      }

      syncVariantInput(variant) {
        const input = this.variantIdInput;
        if (!input || !variant?.id) return;

        input.value = variant.id.toString();
        if (variant.available) {
          input.removeAttribute('disabled');
        } else {
          input.disabled = true;
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getSelectedVariantFromPicker() {
        const productInfo = this.closest('product-info');
        const selectedVariant = productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (!selectedVariant) return null;

        try {
          return JSON.parse(selectedVariant);
        } catch {
          return null;
        }
      }

      ensureVariantInputMatchesSelection() {
        const variant = this.getSelectedVariantFromPicker();
        if (!variant?.id) return;

        const input = this.variantIdInput;
        if (!input) return;

        const variantId = variant.id.toString();
        if (input.value === variantId) {
          if (variant.available) {
            input.removeAttribute('disabled');
          } else {
            input.disabled = true;
          }
          return;
        }

        this.syncVariantInput(variant);
      }

      static SUBSCRIPTION_DESCRIPTION_SELECTORS = [
        '.appstleSelectedSellingPlanOptionDescription',
        '.appstle-frequency-description',
        '.appstle_subscription_description_text',
      ];

      bindPurchaseOptionCardClicks() {
        const anchor = this.form?.querySelector('[data-subscription-widget-anchor]');
        if (!anchor || anchor.dataset.msmPurchaseCardsBound === '1') return;
        anchor.dataset.msmPurchaseCardsBound = '1';

        this.onPurchaseOptionCardClick = this.onPurchaseOptionCardClick.bind(this);
        anchor.addEventListener('click', this.onPurchaseOptionCardClick, true);

        const scheduleRelocate = () => this.relocateSubscriptionWidget();

        document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', scheduleRelocate);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', scheduleRelocate);

        if (window.jQuery) {
          window.jQuery(document).on('appstle_widget_updated', scheduleRelocate);
        }

        scheduleRelocate();
      }

      onPurchaseOptionCardClick(event) {
        if (this._msmPurchaseOptionSelecting) return;

        const anchor = event.currentTarget;
        const widget = anchor.querySelector('.appstle_sub_widget, [id^="appstle_subscription_widget"]');
        if (!widget?.contains(event.target)) return;

        if (event.target.closest('.appstle_tooltip_wrapper')) return;
        if (event.target.closest('select, option, .appstle_custom_select, .appstle_select')) return;
        if (event.target.closest('a, button')) return;
        if (event.target.matches('input[type="radio"][name="selling_plan"]')) return;

        const card = event.target.closest('.appstle_subscription_wrapper_option');
        if (!card || !widget.contains(card)) return;

        const radio = card.querySelector('input[type="radio"][name="selling_plan"]');
        if (!radio) return;

        this._msmPurchaseOptionSelecting = true;
        try {
          radio.click();
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          window.requestAnimationFrame(() => {
            this._msmPurchaseOptionSelecting = false;
          });
        }
      }

      bindSubscriptionTooltipDelay() {
        const anchor = this.form?.querySelector('[data-subscription-widget-anchor]');
        if (!anchor) return;

        this.tooltipShowDelayMs = 350;
        this.setupSubscriptionTooltipDelays = this.setupSubscriptionTooltipDelays.bind(this);

        const scheduleSetup = () => {
          if (this.subscriptionTooltipDelayFrame) return;
          this.subscriptionTooltipDelayFrame = window.requestAnimationFrame(() => {
            this.subscriptionTooltipDelayFrame = null;
            this.setupSubscriptionTooltipDelays();
          });
        };

        if (anchor.dataset.msmTooltipDelayBound !== '1') {
          anchor.dataset.msmTooltipDelayBound = '1';
          document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', scheduleSetup);
          document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', scheduleSetup);

          if (window.jQuery) {
            window.jQuery(document).on('appstle_widget_updated', scheduleSetup);
          }
        }

        scheduleSetup();
      }

      bindSubscriptionDescriptionSanitizer() {
        const scheduleSanitize = () => {
          if (this.subscriptionDescriptionSanitizeFrame) return;
          this.subscriptionDescriptionSanitizeFrame = window.requestAnimationFrame(() => {
            this.subscriptionDescriptionSanitizeFrame = null;
            this.sanitizeSubscriptionDescriptions();
          });
        };

        const scheduleSanitizeWithFollowUp = () => {
          scheduleSanitize();
          window.setTimeout(scheduleSanitize, 50);
          window.setTimeout(scheduleSanitize, 200);
        };

        document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', scheduleSanitizeWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', scheduleSanitizeWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:sellingPlanChanged', scheduleSanitizeWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', scheduleSanitizeWithFollowUp);

        if (window.jQuery) {
          window.jQuery(document).on('appstle_widget_updated', scheduleSanitizeWithFollowUp);
        }

        this.form?.addEventListener('change', (event) => {
          if (event.target?.closest?.('.appstle_select, .appstle_custom_select, select[name="selling_plan"]')) {
            scheduleSanitizeWithFollowUp();
          }
        });

        const anchor = this.form?.querySelector('[data-subscription-widget-anchor]');
        if (anchor && window.MutationObserver && anchor.dataset.msmDescriptionSanitizerBound !== '1') {
          anchor.dataset.msmDescriptionSanitizerBound = '1';
          this.subscriptionDescriptionObserver = new MutationObserver(scheduleSanitizeWithFollowUp);
          this.subscriptionDescriptionObserver.observe(anchor, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        scheduleSanitizeWithFollowUp();
      }

      subscriptionDescriptionNeedsSanitize(element) {
        const html = element.innerHTML;
        if (!html.trim()) return false;

        if (/<\s*(style|meta|title|link|script)\b/i.test(html)) return true;
        if (/Cocoa HTML Writer/i.test(html)) return true;
        if (/p\.p\d*\s*\{/i.test(html)) return true;

        const text = element.textContent || '';
        return /p\.p\d*\s*\{/.test(text) || /Helvetica Neue/.test(text);
      }

      extractSubscriptionDescriptionText(html) {
        if (!html || typeof html !== 'string') return '';

        const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const container = doc.body.firstElementChild;
        if (!container) return '';

        container.querySelectorAll('style, meta, title, link, script').forEach((node) => node.remove());

        const paragraphs = Array.from(container.querySelectorAll('p'))
          .map((paragraph) => paragraph.textContent.replace(/\s+/g, ' ').trim())
          .filter((text) => text && !/^\s*p\.p\d*\s*\{/.test(text) && !/Helvetica Neue/.test(text));

        let text = paragraphs.join(' ').trim();

        if (!text) {
          text = (container.textContent || '')
            .replace(/\bp\.p\d*\s*\{[^}]*\}/gi, '')
            .replace(/\s+/g, ' ')
            .trim();
        }

        return text.replace(/\bp\.p\d*\s*\{[^}]*\}/gi, '').replace(/\s+/g, ' ').trim();
      }

      sanitizeSubscriptionDescriptionElement(element) {
        if (!element || !this.subscriptionDescriptionNeedsSanitize(element)) return;

        const cleanText = this.extractSubscriptionDescriptionText(element.innerHTML);
        if (!cleanText) return;

        element.replaceChildren();
        const paragraph = document.createElement('p');
        paragraph.textContent = cleanText;
        element.appendChild(paragraph);
      }

      sanitizeSubscriptionDescriptions() {
        const scope =
          this.getSubscriptionWidgetRoot() ||
          this.form?.querySelector('[data-subscription-widget-anchor]');
        if (!scope) return;

        this.constructor.SUBSCRIPTION_DESCRIPTION_SELECTORS.forEach((selector) => {
          scope.querySelectorAll(selector).forEach((element) => {
            this.sanitizeSubscriptionDescriptionElement(element);
          });
        });
      }

      getSubscriptionWidgetRoot() {
        const anchor = this.form?.querySelector('[data-subscription-widget-anchor]');
        return (
          this.findSubscriptionWidget() ||
          anchor?.querySelector('.appstle_sub_widget, [id^="appstle_subscription_widget"]')
        );
      }

      setupSubscriptionTooltipDelays() {
        const anchor = this.form?.querySelector('[data-subscription-widget-anchor]');
        if (!anchor) return;

        anchor.querySelectorAll('.appstle_tooltip_wrapper:not([data-msm-tooltip-delay-bound])').forEach((wrapper) => {
          wrapper.dataset.msmTooltipDelayBound = '1';
          const parent = wrapper.closest('.appstle-tootltip-parent');
          if (!parent) return;

          const tooltip = parent.querySelector('.appstle_tooltip');
          const hideTooltip = () => {
            window.clearTimeout(wrapper._msmTooltipShowTimer);
            wrapper._msmTooltipShowTimer = null;
            parent.classList.remove('msm-tooltip-delayed-show');
          };

          wrapper.addEventListener('mouseenter', () => {
            window.clearTimeout(wrapper._msmTooltipShowTimer);
            wrapper._msmTooltipShowTimer = window.setTimeout(() => {
              wrapper._msmTooltipShowTimer = null;
              parent.classList.add('msm-tooltip-delayed-show');
            }, this.tooltipShowDelayMs);
          });

          wrapper.addEventListener('mouseleave', (event) => {
            const related = event.relatedTarget;
            if (related && (wrapper.contains(related) || tooltip?.contains(related))) return;
            hideTooltip();
          });

          parent.addEventListener('mouseleave', (event) => {
            const related = event.relatedTarget;
            if (related && parent.contains(related)) return;
            hideTooltip();
          });
        });
      }

      bindSubscriptionPriceDisplay() {
        const scheduleRestore = () => {
          if (this.subscriptionPriceRestoreFrame) return;
          this.subscriptionPriceRestoreFrame = window.requestAnimationFrame(() => {
            this.subscriptionPriceRestoreFrame = null;
            this.restoreMainProductPrice();
          });
        };

        const scheduleRestoreWithFollowUp = () => {
          scheduleRestore();
          window.setTimeout(scheduleRestore, 100);
          window.setTimeout(scheduleRestore, 500);
        };

        document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', scheduleRestoreWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', scheduleRestoreWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:sellingPlanChanged', scheduleRestoreWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanSelected', scheduleRestoreWithFollowUp);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected', scheduleRestoreWithFollowUp);

        if (window.jQuery) {
          window.jQuery(document).on('appstle_widget_updated', scheduleRestoreWithFollowUp);
        }

        this.form?.addEventListener('change', (event) => {
          if (event.target?.name === 'selling_plan') scheduleRestoreWithFollowUp();
        });

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const productInfo = this.closest('product-info');
            if (!productInfo || event.data?.sectionId !== productInfo.dataset.section) return;
            scheduleRestoreWithFollowUp();
          });
        }

        const priceEl = this.getMainPriceElement();
        if (priceEl && window.MutationObserver) {
          this.subscriptionPriceObserver = new MutationObserver(scheduleRestore);
          this.subscriptionPriceObserver.observe(priceEl, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'data-appstle-price-modified'],
          });
        }

        scheduleRestoreWithFollowUp();
      }

      getMainPriceElement() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;

        const sectionId = productInfo.dataset.section;
        if (sectionId) {
          const priceWrapper = productInfo.querySelector(`#price-${sectionId} .price`);
          if (priceWrapper) return priceWrapper;
        }

        return productInfo.querySelector('.price');
      }

      restoreMainProductPrice() {
        const priceEl = this.getMainPriceElement();
        if (!priceEl) return;

        const container = priceEl.querySelector('.price__container--product');
        if (!container) return;

        const appstleSelectors = [
          '.appstle_subscription_final_price',
          '.appstle_subscription_element',
          '.appstle_subscription_compare_price',
          '.appstle_subscription_unit_price',
          '.appstle-prepaid-price',
          '.appstle-prepaid-current-price',
          '.appstle-prepaid-original-price',
          '.appstle-prepaid-total-price',
        ];

        appstleSelectors.forEach((selector) => {
          container.querySelectorAll(selector).forEach((node) => node.remove());
        });

        container.querySelectorAll('[data-appstle-price-modified]').forEach((node) => {
          node.removeAttribute('data-appstle-price-modified');
          node.style.removeProperty('text-decoration');
          node.style.removeProperty('color');
        });
      }

      bindSubscriptionWidgetEvents() {
        const scheduleRelocate = () => {
          if (this.subscriptionRelocateFrame) return;
          this.subscriptionRelocateFrame = window.requestAnimationFrame(() => {
            this.subscriptionRelocateFrame = null;
            this.relocateSubscriptionWidget();
          });
        };

        document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', scheduleRelocate);
        document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', scheduleRelocate);

        if (window.jQuery) {
          window.jQuery(document).on('appstle_widget_updated', scheduleRelocate);
        }

        if (window.MutationObserver && this.form) {
          this.subscriptionWidgetObserver = new MutationObserver(scheduleRelocate);
          this.subscriptionWidgetObserver.observe(this.form, { childList: true, subtree: true });
        }
      }

      nodeMatchesAppstle(node) {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;

        const id = node.id || '';
        let className = '';
        if (typeof node.className === 'string') {
          className = node.className;
        } else if (node.className && node.className.baseVal) {
          className = node.className.baseVal;
        }

        return /appstle/i.test(id) || /appstle/i.test(className);
      }

      nodeIsOrContainsAppstle(node) {
        if (this.nodeMatchesAppstle(node)) return true;
        return !!node.querySelector('[class*="appstle"], [class*="Appstle"], [id*="appstle"], [id*="Appstle"]');
      }

      findAppstleWidgetRoot(node, form, anchor, row, buttons) {
        if (!node || !form.contains(node)) return null;
        if (anchor.contains(node) || row.contains(node) || buttons.contains(node)) return null;

        let current = node;
        while (current && current.parentElement && current.parentElement !== form) {
          current = current.parentElement;
        }

        if (!current || current.parentElement !== form) return null;
        if (anchor.contains(current) || row.contains(current) || buttons.contains(current)) return null;
        return current;
      }

      findSubscriptionWidget() {
        if (!this.form) return null;

        const anchor = this.form.querySelector('[data-subscription-widget-anchor]');
        const row = this.form.querySelector('.product-form__row');
        const buttons = this.form.querySelector('.product-form__buttons');
        if (!anchor || !row) return null;

        let node = row.nextElementSibling;
        while (node && node !== buttons) {
          if (this.nodeIsOrContainsAppstle(node)) {
            return node;
          }
          node = node.nextElementSibling;
        }

        const matches = this.form.querySelectorAll('[class*="appstle"], [class*="Appstle"], [id*="appstle"], [id*="Appstle"]');
        for (let i = 0; i < matches.length; i++) {
          const root = this.findAppstleWidgetRoot(matches[i], this.form, anchor, row, buttons);
          if (root) return root;
        }

        return null;
      }

      relocateSubscriptionWidget() {
        if (!this.form) return;

        const anchor = this.form.querySelector('[data-subscription-widget-anchor]');
        const row = this.form.querySelector('.product-form__row');
        if (!anchor || !row) return;

        const widget = this.findSubscriptionWidget();
        if (!widget || anchor.contains(widget)) return;

        anchor.appendChild(widget);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.ensureVariantInputMatchesSelection();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
  .then(async (response) => {
    // ✅ Handle partial add or error
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { description: 'Could not add requested quantity.' };
      }

      const message = errorData.description || 'Could not add requested quantity.';
      this.handleErrorMessage(message);

      // ✅ Refresh drawer first
      if (this.cart && typeof this.cart.refreshDrawerOnly === 'function') {
        await this.cart.refreshDrawerOnly();
      }

      // ✅ Force bubble update with slight delay so cart.js reflects correct count
      setTimeout(() => {
        refreshCartBubbleOnly();
      }, 200);

      // ✅ Soft warning → keep button active
      if (
        message.toLowerCase().includes('availability') ||
        message.toLowerCase().includes('maximum quantity')
      ) {
        this.error = false;
      } else {
        this.error = true; // hard error → disable
      }

      return null; // stop success flow
    }

    // ✅ Normal success → parse JSON
    return response.json();
  })
  .then(async (response) => {
    if (!response) return; // bailed due to error above

    const quickAddModal = this.closest('quick-add-modal');
    if (quickAddModal) {
      document.body.addEventListener(
        'modalClosed',
        () => {
          setTimeout(() => {
            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          });
        },
        { once: true }
      );
      quickAddModal.hide(true);
    } else if (this.cart) {
      CartPerformance.measure('add:paint-updated-sections', () => {
        this.cart.renderContents(response);
      });
    }

    const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-form',
      productVariantId: formData.get('id'),
      cartData: response,
    }).then(() => {
      CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
    });
  })
  .catch((e) => {
    console.error('Cart add failed:', e);
  })
  .finally(() => {
    this.submitButton.classList.remove('loading');
    if (this.cart && this.cart.classList.contains('is-empty')) {
      this.cart.classList.remove('is-empty');
    }
    if (!this.error) this.submitButton.removeAttribute('aria-disabled');
    this.querySelector('.loading__spinner').classList.add('hidden');

    CartPerformance.measureFromEvent('add:user-action', evt);
  });

      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
